import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';

/**
 * Speedometer-style gauge for the risk analysis dashboard card.
 *
 * The arc is split into three equal coloured zones (each one third):
 *   - LOW (green, left) - MEDIUM (yellow, centre) - HIGH (red, right).
 *
 * The needle is placed on the count-weighted position of all records, so it
 * moves left when records are low-risk and right when they are high-risk:
 *   position = (LOW*0 + MEDIUM*0.5 + HIGH*1) / total   (0 = far left, 1 = far right)
 * All high -> far right, all low -> far left, half/half -> middle.
 */
@Component({
  selector: 'app-risk-gauge',
  templateUrl: './risk-gauge.component.html',
  styleUrls: ['./risk-gauge.component.css'],
  standalone: false
})
export class RiskGaugeComponent implements OnChanges {
  @Input() riskAnalysis: any[] = [];

  // Gauge geometry (SVG user units).
  readonly cx = 100;
  readonly cy = 100;
  readonly r = 80;

  // Zone boundaries on the 1-25 figure scale (used only to classify records).
  private readonly lowMax = 6;
  private readonly mediumMax = 14;

  // Risk-figure palette (shared with setRiskFigure()).
  readonly greenColor = '#4ec55c';
  readonly yellowColor = '#f0ac19';
  readonly redColor = '#e7644f';

  hasData = false;
  zoneText = '';
  zoneColor = '';

  // Per-zone record counts (distribution shown under the gauge).
  lowCount = 0;
  mediumCount = 0;
  highCount = 0;

  // Hover tooltip state.
  @ViewChild('gauge', { static: true }) gaugeRef!: ElementRef<HTMLElement>;
  tipVisible = false;
  tipZoneKey: 'LOW' | 'MEDIUM' | 'HIGH' = 'LOW';
  tipCount = 0;
  tipX = 0;
  tipY = 0;

  // Pre-computed SVG paths for the three equal coloured arcs.
  greenArc = '';
  yellowArc = '';
  redArc = '';

  // Needle end-point + its resting position (0..1) for hover restore.
  needleX = this.cx;
  needleY = this.cy;
  private position = 0;

  // Centre position (0..1) of each zone third, for the hover needle.
  private readonly zoneCenter = { LOW: 1 / 6, MEDIUM: 1 / 2, HIGH: 5 / 6 };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['riskAnalysis']) {
      this.buildArcs();
      this.compute();
    }
  }

  /**
   * Builds the three equal coloured arcs (each a third of the 180deg semicircle).
   */
  private buildArcs(): void {
    this.greenArc = this.describeArc(180, 120);
    this.yellowArc = this.describeArc(120, 60);
    this.redArc = this.describeArc(60, 0);
  }

  /**
   * Counts records per zone and positions the needle on the count-weighted mean.
   */
  private compute(): void {
    this.countZones();
    const total = this.lowCount + this.mediumCount + this.highCount;
    this.hasData = total > 0;

    if (!this.hasData) {
      this.position = 0;
      this.setNeedle(0);
      return;
    }

    this.position =
      (this.mediumCount * 0.5 + this.highCount) / total;
    this.applyZone(this.position);
    this.setNeedle(this.position);
  }

  /**
   * Counts records per risk zone. Uses the backend-computed bucket
   * (RiskFigureData.ValueText) when present, else classifies P x C.
   */
  private countZones(): void {
    this.lowCount = 0;
    this.mediumCount = 0;
    this.highCount = 0;
    for (const record of this.riskAnalysis || []) {
      switch (this.zoneOf(record)) {
        case 'LOW':
          this.lowCount++;
          break;
        case 'MEDIUM':
          this.mediumCount++;
          break;
        case 'HIGH':
          this.highCount++;
          break;
      }
    }
  }

  private zoneOf(record: any): 'LOW' | 'MEDIUM' | 'HIGH' | null {
    const valueText = record?.RiskFigureData?.ValueText;
    if (valueText) {
      const upper = String(valueText).toUpperCase();
      if (upper === 'LOW' || upper === 'MEDIUM' || upper === 'HIGH') {
        return upper;
      }
    }
    const figure = this.toFigure(record);
    if (figure === null) {
      return null;
    }
    if (figure <= this.lowMax) return 'LOW';
    if (figure <= this.mediumMax) return 'MEDIUM';
    return 'HIGH';
  }

  /**
   * Extracts a valid Probability x Consequence figure from a record, or null.
   */
  private toFigure(record: any): number | null {
    const p = Number(record?.Probability);
    const c = Number(record?.Consequence);
    if (!p || !c || isNaN(p) || isNaN(c)) {
      return null;
    }
    return p * c;
  }

  /**
   * Sets the zone label/colour from the needle position (0..1) split in thirds.
   */
  private applyZone(position: number): void {
    if (position < 1 / 3) {
      this.zoneText = 'LOW';
      this.zoneColor = this.greenColor;
    } else if (position < 2 / 3) {
      this.zoneText = 'MEDIUM';
      this.zoneColor = this.yellowColor;
    } else {
      this.zoneText = 'HIGH';
      this.zoneColor = this.redColor;
    }
  }

  private polar(angleDeg: number, radius: number): { x: number; y: number } {
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: this.cx + radius * Math.cos(rad),
      y: this.cy - radius * Math.sin(rad)
    };
  }

  /**
   * SVG arc path between two angles along the top semicircle (left to right).
   */
  private describeArc(fromAngle: number, toAngle: number): string {
    const start = this.polar(fromAngle, this.r);
    const end = this.polar(toAngle, this.r);
    return `M ${start.x} ${start.y} A ${this.r} ${this.r} 0 0 1 ${end.x} ${end.y}`;
  }

  /**
   * Places the needle at a position (0 = far left/180deg, 1 = far right/0deg).
   */
  private setNeedle(position: number): void {
    const clamped = Math.min(1, Math.max(0, position));
    const point = this.polar(180 - clamped * 180, this.r * 0.82);
    this.needleX = point.x;
    this.needleY = point.y;
  }

  /**
   * Moves the needle to the centre of the hovered zone and shows the tooltip.
   */
  onZoneEnter(zone: 'LOW' | 'MEDIUM' | 'HIGH'): void {
    this.setNeedle(this.zoneCenter[zone]);
    this.tipZoneKey = zone;
    this.tipCount =
      zone === 'LOW'
        ? this.lowCount
        : zone === 'MEDIUM'
        ? this.mediumCount
        : this.highCount;
    this.tipVisible = true;
  }

  /**
   * Positions the tooltip near the cursor.
   */
  onZoneMove(event: MouseEvent): void {
    const rect = this.gaugeRef.nativeElement.getBoundingClientRect();
    this.tipX = event.clientX - rect.left;
    this.tipY = event.clientY - rect.top;
  }

  /**
   * Hides the tooltip and returns the needle to its resting position.
   */
  onZoneLeave(): void {
    this.tipVisible = false;
    this.setNeedle(this.hasData ? this.position : 0);
  }
}
